import React from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { experts } from '../data/experts';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemText,
  Link,
  Stack,
  Chip,
  Paper,
  SvgIcon
} from '@mui/material';
// Import our custom Grid component instead
import Grid from '../components/Grid';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LanguageIcon from '@mui/icons-material/Language';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// Import the ExpertTweets component
import ExpertTweets from '../components/ExpertTweets';

const ExpertDetailPage: React.FC = () => {
  const { expertId } = useParams<{ expertId: string }>();
  const expert = experts.find(e => e.id === expertId);

  if (!expert) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>Expert Not Found</Typography>
        <Typography sx={{ mb: 2 }}>We couldn't find the expert you're looking for.</Typography>
        <Button
          variant="contained"
          component={RouterLink}
          to="/experts"
          startIcon={<ArrowBackIcon />}
        >
          Back to Experts
        </Button>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider', pb: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom color="secondary.main">
          {expert.name}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {expert.role}
          {expert.organization && ` at ${expert.organization}`}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Main Content Column */}
        {/* Ensure 'item' prop is present */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Image */}
            {expert.imageUrl && (
              <Box sx={{ mb: 2 }}>
                <CardMedia
                  component="img"
                  image={expert.imageUrl}
                  alt={expert.name}
                  sx={{ borderRadius: 1, width: '100%', maxHeight: 400, objectFit: 'cover' }}
                />
              </Box>
            )}

            {/* About Section */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>About {expert.name}</Typography>
                <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
                  {expert.bio || expert.shortBio}
                </Typography>

                {expert.expertise && expert.expertise.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Areas of Expertise:</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {expert.expertise.map((area, index) => (
                        <Chip key={index} label={area} size="small" />
                      ))}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Publications Section - Moved here between About and Connect */}
            {expert.publications && expert.publications.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Publications</Typography>
                  <List dense disablePadding>
                    {expert.publications.map((pub, index) => (
                      <ListItem key={index} disableGutters divider={expert.publications && index < expert.publications.length - 1}>
                        <ListItemText
                          primary={<Link href={pub.url} target="_blank" rel="noopener noreferrer" underline="hover">{pub.title}</Link>}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}

            {/* Connect Section */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Connect & Follow</Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  {expert.twitter && (
                    <Button
                      variant="outlined"
                      startIcon={
                        <SvgIcon>
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </SvgIcon>
                      }
                      href={`https://x.com/${expert.twitter}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      
                    </Button>
                  )}
                  {expert.linkedIn && (
                    <Button
                      variant="outlined"
                      startIcon={<LinkedInIcon />}
                      href={expert.linkedIn}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      LinkedIn
                    </Button>
                  )}
                  {expert.website && (
                    <Button
                      variant="outlined"
                      startIcon={<LanguageIcon />}
                      href={expert.website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Website
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Sidebar Column */}
        {/* Ensure 'item' prop is present */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            {/* Latest Insights/Commentary */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Latest Insights</Typography>
                <Typography variant="body2" paragraph>
                  Stay updated with {expert.name}'s latest market insights and analysis.
                </Typography>
                
                {expert.twitter ? (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Recent Market Commentary:</Typography>
                    
                    {/* Display the expert's tweets */}
                    <ExpertTweets twitterHandle={expert.twitter} />
                    
                    {/* Link to full X profile */}
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={
                          <SvgIcon>
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                          </SvgIcon>
                        }
                        href={`https:///${expert.twitter}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View more on X
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No direct feed available. Check their website or LinkedIn for updates.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Back Button */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          variant="outlined"
          component={RouterLink}
          to="/experts"
          startIcon={<ArrowBackIcon />}
        >
          Back to Experts Directory
        </Button>
      </Box>
    </Box>
  );
};

export default ExpertDetailPage;